import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Box, Chip, CircularProgress, Divider, Typography } from '@mui/material';

import { getRoomById } from '@src/api/room';

export default function RoomDisplay() {
  const { id } = useParams<{ id: string }>();
  const [room, setRoom] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;

    getRoomById(id)
      .then(data => setRoom(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!room) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography>Room not found.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 600,
        mx: 'auto',
        mt: 6,
        p: 4,
        borderRadius: 3,
        boxShadow: 3,
        backgroundColor: '#fff',
        fontFamily: 'Lato',
      }}
    >
      <Typography variant='h4' sx={{ fontFamily: 'Lato', fontWeight: 700, color: '#0f222e', mb: 1 }}>
        Room {room.roomNumber}
      </Typography>

      {room.roomDescription && (
        <Typography sx={{ fontFamily: 'Lato', color: '#555', mb: 2 }}>{room.roomDescription}</Typography>
      )}

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <Detail label='Location' value={room.location?.name} />
        <Detail label='Floor' value={room.floorNumber} />
        <Detail label='Type' value={room.type} />
        <Detail label='Capacity' value={room.capacity} />
        <Detail label='Media Support' value={room.mediaSupport} />
        <Detail label='Max Time Limit' value={room.maxTimeLimit} />
        {room.startTime && <Detail label='Start Time' value={room.startTime} />}
        {room.endTime && <Detail label='End Time' value={room.endTime} />}
      </Box>

      {room.isSupportName && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant='h6' sx={{ fontFamily: 'Lato', fontWeight: 700, mb: 2 }}>
            IT Support
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {room.isSupportName && <Detail label='Name' value={room.isSupportName} />}
            {room.isSupportEmail && <Detail label='Email' value={room.isSupportEmail} />}
          </Box>
        </>
      )}

      {room.locationAddress && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant='h6' sx={{ fontFamily: 'Lato', fontWeight: 700, mb: 2 }}>
            Location Info
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {room.locationAddress && <Detail label='Address' value={room.locationAddress} />}
            {room.locationLandmark && <Detail label='Landmark' value={room.locationLandmark} />}
            {room.locationContactPerson && <Detail label='Contact' value={room.locationContactPerson} />}
            {room.locationPhoneNumber && <Detail label='Phone' value={room.locationPhoneNumber} />}
          </Box>
        </>
      )}

      {Array.isArray(room.roomDirection) && room.roomDirection.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant='h6' sx={{ fontFamily: 'Lato', fontWeight: 700, mb: 1 }}>
            Directions
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {room.roomDirection.map((d: string, i: number) => (
              <Chip key={i} label={d} />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

function Detail({ label, value }: { label: string; value: any }) {
  if (!value && value !== 0) return null;

  return (
    <Box>
      <Typography
        sx={{ fontFamily: 'Lato', fontSize: '12px', color: '#888', fontWeight: 600, textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontFamily: 'Lato', fontSize: '15px', color: '#0f222e', fontWeight: 500 }}>{value}</Typography>
    </Box>
  );
}
